import React, { useState, memo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import TableLine from "./TableLine/TableLine";

const CATEGORY_HELP_TOOLTIP =
  "To add or edit categories (and set budgets), go to Settings → Categories.";

const CategoryHelpIcon = () => (
  <Link
    to="/settings?expand=categories"
    className="ml-1 inline-flex text-slate-400 transition-colors hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded"
    title={CATEGORY_HELP_TOOLTIP}
    aria-label={CATEGORY_HELP_TOOLTIP}
  >
    <svg
      className="h-4 w-4"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  </Link>
);

// Memoized row component for better performance
const MemoizedTableLine = memo(TableLine);

// Skeleton loading row for desktop table
const SkeletonTableRow = ({ showPayments }) => (
  <tr className="animate-pulse">
    <td className="border-b border-slate-200 px-4 py-3">
      <div className="h-5 w-32 rounded bg-slate-200"></div>
    </td>
    <td className="border-b border-slate-200 px-4 py-3">
      <div className="h-5 w-24 rounded bg-slate-200"></div>
    </td>
    {showPayments && (
      <>
        <td className="border-b border-slate-200 px-4 py-3">
          <div className="h-5 w-16 rounded bg-slate-200"></div>
        </td>
        <td className="border-b border-slate-200 px-4 py-3">
          <div className="h-5 w-20 rounded bg-slate-200"></div>
        </td>
      </>
    )}
    <td className="border-b border-slate-200 px-4 py-3">
      <div className="h-5 w-20 rounded bg-slate-200"></div>
    </td>
    <td className="border-b border-slate-200 px-4 py-3">
      <div className="h-8 w-16 rounded bg-slate-200"></div>
    </td>
  </tr>
);

// Skeleton loading card for mobile
const SkeletonCard = () => (
  <div className="animate-pulse rounded-lg border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between">
      <div className="h-5 w-32 rounded bg-slate-200"></div>
      <div className="h-5 w-20 rounded bg-slate-200"></div>
    </div>
    <div className="mt-2 h-4 w-24 rounded bg-slate-200"></div>
  </div>
);

const TableInput = (props) => {
  const [addClicked, setAddClicked] = useState(false);
  const [hasScrolledInitially, setHasScrolledInitially] = useState(false);
  const listEndRef = useRef(null);
  const showPayments = props.title === "Fixed Expenses";

  // Scroll to bottom when data loads initially (for each category)
  useEffect(() => {
    if (props.data && props.data.length > 0 && !hasScrolledInitially) {
      setTimeout(() => {
        listEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
        setHasScrolledInitially(true);
      }, 150);
    }
  }, [props.data, hasScrolledInitially]);

  // Reset scroll flag when navigating to a different category
  useEffect(() => {
    setHasScrolledInitially(false);
  }, [props.title]);

  const scrollToBottom = () => {
    setTimeout(() => {
      listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 100);
  };

  const handleAdd = async () => {
    setAddClicked(true);
    await props.onChange(props.title, "add", "", "");
    setAddClicked(false);
    scrollToBottom();
  };

  return (
    <div className="relative pb-4">
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-100 text-left text-sm font-semibold text-slate-700">
                <th className="border-b border-slate-200 px-4 py-3">
                  Title
                </th>
                <th className="border-b border-slate-200 px-4 py-3">
                  <span className="inline-flex items-center">
                    Category
                    <CategoryHelpIcon />
                  </span>
                </th>
                {showPayments && (
                  <>
                    <th className="border-b border-slate-200 px-4 py-3">
                      Payments
                    </th>
                    <th className="border-b border-slate-200 px-4 py-3">
                      Monthly
                    </th>
                  </>
                )}
                <th className="border-b border-slate-200 px-4 py-3">
                  Amount ({props.symbol})
                </th>
                {props.remove_enabled && (
                  <th className="w-24 border-b border-slate-200 px-4 py-3">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {props.data.map((line, key) => (
                <MemoizedTableLine
                  key={line.id || key}
                  index={key}
                  title={line.title}
                  categories={props.categories}
                  category={line.category}
                  sum={line.sum}
                  name={props.title}
                  onChange={props.onChange}
                  id={line.id}
                  screeWidth={props.screeWidth}
                  delete_rec_by_id={props.delete_rec_by_id}
                  table_title={props.title}
                  from_date={line.from_date}
                  to_date={line.to_date}
                  updated={line.updated}
                  is_payment={line.is_payment}
                  payments={line.payments}
                  recored_updated={line.recored_updated}
                  saveUpdateValueHandler={props.saveUpdateValueHandler}
                  remove_enabled={props.remove_enabled}
                  symbol={props.symbol}
                  isDesktop={true}
                  showPayments={showPayments}
                  expenseId={props.expenseId}
                  categoriesWithBudget={props.categoriesWithBudget}
                  currentDate={props.currentDate}
                  selectDateHandler={props.selectDateHandler}
                />
              ))}
              {addClicked && <SkeletonTableRow showPayments={showPayments} />}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="space-y-3 md:hidden">
        {props.data.map((line, key) => (
          <MemoizedTableLine
            key={line.id || key}
            index={key}
            title={line.title}
            categories={props.categories}
            category={line.category}
            sum={line.sum}
            name={props.title}
            onChange={props.onChange}
            id={line.id}
            screeWidth={props.screeWidth}
            delete_rec_by_id={props.delete_rec_by_id}
            table_title={props.title}
            from_date={line.from_date}
            to_date={line.to_date}
            updated={line.updated}
            is_payment={line.is_payment}
            payments={line.payments}
            recored_updated={line.recored_updated}
            saveUpdateValueHandler={props.saveUpdateValueHandler}
            remove_enabled={props.remove_enabled}
            symbol={props.symbol}
            isDesktop={false}
            showPayments={showPayments}
            expenseId={props.expenseId}
            categoriesWithBudget={props.categoriesWithBudget}
            currentDate={props.currentDate}
            selectDateHandler={props.selectDateHandler}
          />
        ))}
        {addClicked && <SkeletonCard />}
      </div>

      {/* Scroll anchor */}
      <div ref={listEndRef} />

      {/* Floating Action Button */}
      <div className="pointer-events-none fixed bottom-6 right-6 z-50 flex justify-end xl:right-[calc(15%+1.5rem)]">
        <button
          type="button"
          onClick={handleAdd}
          disabled={addClicked}
          className="pointer-events-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg transition-all duration-200 hover:bg-indigo-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-70"
          aria-label="Add new item"
        >
          <svg
            className="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default TableInput;
